import { useEffect, useState } from 'react';
import {
  TokenAsset,
  Network,
  Wallet,
  CronosChain,
  WalletType,
  EthChainId,
} from '@types';
import { generateSigner } from '@lib/utils';
import { getExtraInfo as getCronosExtraInfo } from '@lib/cronos-chain';
import { getErc20Contract, getErc20Allowance } from '@lib/gravity-bridge';
import useNetworkEnv from '@hooks/useNetworkEnv';
import { notify } from '@lib/bugsnag';
import { NotifiableError } from '@bugsnag/core';
import { BigNumber, utils } from 'ethers';

interface ShouldGetERC20ApprovalParams {
  wallet: Wallet;
  amount: string;
  tokenAsset: TokenAsset | null;
  fromNetwork: Network;
  toNetwork: Network;
  toAddress: string;
}

interface ApprovalState {
  shouldGetERC20Approval: boolean;
  checkingERC20Allowance: boolean;
  allowanceError: unknown | null;
}

function useShouldGetERC20Approval({
  wallet,
  amount,
  tokenAsset,
  fromNetwork,
  toNetwork,
  toAddress,
}: ShouldGetERC20ApprovalParams): ApprovalState {
  const [isTestnet] = useNetworkEnv();
  const [approvalState, setApprovalState] = useState<ApprovalState>({
    shouldGetERC20Approval: false,
    checkingERC20Allowance: false,
    allowanceError: null,
  });

  const updateErrorState = (error: unknown) => {
    setApprovalState((prev) => {
      return {
        ...prev,
        allowanceError: error,
        shouldGetERC20Approval: true,
      };
    });
  };

  // TODO: Improve performance so allowance value can be memoized, reducing number of calls and state change
  useEffect(() => {
    if (!wallet || !wallet.active || !tokenAsset) return;
    if (tokenAsset.address[wallet.chain]?.substring(0, 2) !== '0x') return;
    if (
      (fromNetwork !== Network.Ethereum && toNetwork !== Network.Cronos) ||
      fromNetwork === Network.Cosmos ||
      toNetwork === Network.Cosmos ||
      fromNetwork === Network.Terra ||
      toNetwork === Network.Terra ||
      fromNetwork === Network.Juno ||
      toNetwork === Network.Juno ||
      fromNetwork === Network.Akash ||
      toNetwork === Network.Akash ||
      fromNetwork === Network.Canto ||
      toNetwork === Network.Canto ||
      fromNetwork === Network.Iris ||
      toNetwork === Network.Iris ||
      fromNetwork === Network.Celestia ||
      toNetwork === Network.Celestia
    )
      return;

    // No need if native ethereum
    if (tokenAsset.symbol === 'ETH') return;

    if (Number(amount) === 0 || Number.isNaN(Number(amount))) return;

    async function updateApprovalStatus(
      asset: TokenAsset,
      walletAddress: string
    ) {
      try {
        setApprovalState((prev) => {
          return { ...prev, checkingERC20Allowance: true };
        });

        const contractAddress = getCronosExtraInfo(
          isTestnet ? EthChainId.CronosGravityTestnet : EthChainId.Cronos
        ).contractAddresses.gravity;

        const signer = await generateSigner(wallet);
        const erc20Contract = getErc20Contract(
          asset.address[wallet.chain],
          signer
        );

        // retrieve gravity contract allowance as spender
        const allowance = await getErc20Allowance(
          walletAddress,
          contractAddress,
          erc20Contract
        );

        if (utils.parseUnits(amount, asset.decimals).gt(allowance)) {
          setApprovalState((prev) => {
            return { ...prev, shouldGetERC20Approval: true };
          });
        }
      } catch (error) {
        notify(error as NotifiableError);
        updateErrorState(error);
      } finally {
        setApprovalState((prev) => {
          return { ...prev, checkingERC20Allowance: false };
        });
      }
    }
    if (tokenAsset && wallet.address) {
      updateApprovalStatus(tokenAsset, wallet.address);
    }
  }, [
    wallet,
    amount,
    tokenAsset,
    fromNetwork,
    toNetwork,
    toAddress,
    isTestnet,
  ]);

  return approvalState;
}

export default useShouldGetERC20Approval;
